import React, { useEffect, useState } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { URL } from "../../../utils/Constants";
import { useARRCode } from "../context/ARRLevelContext";
import swal from "sweetalert";

export const useSubjectsCore = () => {
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);

  const getSubjectCore = () => {
    setLoading(true);
    axiosPost(
      URL.getAllSubject,
      {},
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setSubjects(data);
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    getSubjectCore();
  }, []);

  return { subjects, loading };
};

export const useSubjects = () => {
  const { arrLevel } = useARRCode();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState({
    read: false,
    create: false,
    update: false,
    delete: false,
  });

  //read subject
  const getAllSubject = () => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.subjectList,
      {
        assessmentModuleCode: arrLevel,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setSubjects(data);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, read: false }));
        swal("Oops", "Failed to get subjects");
        return;
      },
    );
  };

  //create subject
  const createSubject = (data) => {
    if (!data || !data?.subjectName?.value) {
      swal("Oops", "Subject Name is missing");
      return;
    }

    if (!data.subjectTheory && !data.subjectPractical) {
      swal("Oops", "Subject Type is missing");
      return;
    }

    const jsonData = {
      name: data.subjectName.value,
      hasTheory: data.subjectTheory,
      hasPractical: data.subjectPractical,
      subjectTheoryCode: data?.theoryCode,
      subjectPracticalCode: data?.practicalCode,
      description: data?.description,
      subjectAreaId: data?.subjectArea?.value ?? "",
      assessmentModuleCode: arrLevel,
    };

    const formData = new FormData();

    if (data.subjectImage) {
      formData.append("file", data.subjectImage);
    }

    formData.append("jsonData", JSON.stringify(jsonData));

    setLoading((prev) => ({ ...prev, create: true }));

    return new Promise((resolve, reject) => {
      axiosPost(
        URL.insertSubjectList,
        formData,
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.message || "Successfully created subject",
            );
            resolve(response);
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, create: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, create: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to create subject",
          );
          reject(error);
        },
      );
    });
  };

  //updateSubject
  const updateSubject = (id, data) => {
    if (!data || !data?.subjectName?.value) {
      swal("Oops", "Subject Name is missing");
      return;
    }

    if (!data.subjectTheory && !data.subjectPractical) {
      swal("Oops", "Subject Type is missing");
      return;
    }

    const jsonData = {
      id: id,
      name: data.subjectName.value,
      hasTheory: data.subjectTheory,
      hasPractical: data.subjectPractical,
      subjectTheoryCode: data?.theoryCode ?? "",
      subjectPracticalCode: data?.practicalCode ?? "",
      description: data?.description ?? "",
      subjectAreaId: data?.subjectArea?.value ?? "",
      thumbnailPath: data?.thumbnailPath ?? "",
    };

    const formData = new FormData();

    if (data.subjectImage && !data?.thumbnailPath) {
      formData.append("file", data.subjectImage);
    }

    formData.append("jsonData", JSON.stringify(jsonData));

    setLoading((prev) => ({ ...prev, update: true }));

    return new Promise((resolve, reject) => {
      axiosPost(
        URL.updateSubject,
        formData,
        (response) => {
          if (response.status === 200) {
            swal(
              "Success",
              response?.message || "Successfully updated subject",
            );
            resolve(response);
            getAllSubject();
          } else {
            reject(response);
          }
          setLoading((prev) => ({ ...prev, update: false }));
        },
        (error) => {
          setLoading((prev) => ({ ...prev, update: false }));
          swal(
            "Oops",
            error?.response?.data?.message || "Failed to update subject",
          );
          reject(error);
        },
      );
    });
  };

  //delete subject
  const deleteSubject = (id) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    axiosPost(
      URL.deleteSubjectList,
      {
        id: id,
      },
      (response) => {
        if (response.status === 200) {
          swal("Success", response?.message || "Successfully deleted subject");
          getAllSubject();
        }
        setLoading((prev) => ({ ...prev, delete: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to delete subject",
        );
      },
    );
  };

  useEffect(() => {
    getAllSubject();
  }, [arrLevel]);

  return {
    subjects,
    loading,
    createSubject,
    updateSubject,
    deleteSubject,
    refetchSubjects: getAllSubject,
  };
};

export const useSubjectSettings = ({ subjectId, refetchSubjects }) => {
  const { arrLevel } = useARRCode();
  const [loading, setLoading] = useState({
    read: false,
    create: false,
    update: false,
    delete: false,
  });
  const [subjectsSettings, setSubjectsSettings] = useState([]);

  const getSubjectSettings = () => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.getSubjectSettings,
      {
        assessmentModuleCode: arrLevel,
        subjectId: subjectId,
      },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setSubjectsSettings(data);
        }
        setLoading((prev) => ({ ...prev, read: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const createSubjectSettings = (data) => {
    setLoading((prev) => ({ ...prev, create: true }));
    axiosPost(
      URL.insertSubjectSetting,
      data,
      (response) => {
        if (response.status === 200) {
          swal("Success", "Settings have been successfully saved");
          getSubjectSettings();
        }
        setLoading((prev) => ({ ...prev, create: false }));
      },
      (error) => {
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to save settings",
        );
        setLoading((prev) => ({ ...prev, create: false }));
      },
    );
  };

  useEffect(() => {
    getSubjectSettings();
  }, [arrLevel, subjectId]);

  return { subjectsSettings, loading, createSubjectSettings };
};

export const useRecruitmentStatus = () => {
  const [recruitmentStatus, setRecruitmentStatus] = useState([]);

  const getRecruitmentStatus = () => {
    axiosPost(URL.getRecruitmentStatus, {}, (response) => {
      if (response.status === 200) {
        const data = response?.data?.data;
        setRecruitmentStatus(data);
      }
    });
  };

  useEffect(() => {
    getRecruitmentStatus();
  }, []);

  return { recruitmentStatus };
};

export const useRecruitment = () => {
  const [staffList, setStaffList] = useState([]);

  const getStaffList = (statusId) => {
    axiosPost(
      URL.getStaffList,
      { recruitmentStatusId: statusId },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setStaffList(data);
        }
      },
    );
  };

  return { staffList, getStaffList };
};

export const useYearLevelBySubject = (subjectId) => {
  const [yearLevelBySubject, setYearLevelBySubject] = useState([]);
  const [loading, setLoading] = useState({
    create: false,
    read: false,
    delete: false,
  });

  const getSubjectClassroomTeacher = () => {
    setLoading((prev) => ({ ...prev, read: true }));
    axiosPost(
      URL.getSubjectClassroomTeacher,
      { subjectId: subjectId },
      (response) => {
        if (response.status === 200) {
          const data = response?.data?.data;
          setYearLevelBySubject(data);
        }
        setLoading((prev) => ({ ...prev, read: false }));

        if (response.status === 204) {
          setLoading((prev) => ({ ...prev, read: false }));
        }
      },
      (error) => {
        setLoading((prev) => ({ ...prev, read: false }));
      },
    );
  };

  const insertSubjectClassroomTeacher = (payload) => {
    setLoading((prev) => ({ ...prev, create: true }));
    axiosPost(
      URL.insertSubjectClassroomTeacher,
      payload,
      (response) => {
        if (response.status === 200) {
          swal("Success", "Successfully assigned teachers");
          getSubjectClassroomTeacher(subjectId);
        }
        setLoading((prev) => ({ ...prev, create: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, create: false }));
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to assign teachers",
        );
      },
    );
  };

  const deleteSubjectClassroomTeacher = (payload) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    axiosPost(
      URL.deleteSubjectClassroomTeacher,
      payload,
      (response) => {
        if (response.status === 200) {
          swal("Success", "Successfully unassigned teacher");
          getSubjectClassroomTeacher(subjectId);
        }
        setLoading((prev) => ({ ...prev, delete: false }));
      },
      (error) => {
        setLoading((prev) => ({ ...prev, delete: false }));
        swal(
          "Oops",
          error?.response?.data?.message || "Failed to unassign teacher",
        );
      },
    );
  };

  useEffect(() => {
    getSubjectClassroomTeacher();
  }, [subjectId]);

  return {
    yearLevelBySubject,
    loading,
    insertSubjectClassroomTeacher,
    deleteSubjectClassroomTeacher,
  };
};
